<template>
	<NuxtLayout>
		<div class="availabilityContact container" v-if="!isFormSuccess">
			<h1 v-text="page?.title" />
			<h2 v-text="$t('availabilityContact.lead')" />

			<form @submit.prevent="submitEnquiry" class="absoluteLabelsForm availabilityContact__Form">
				<FieldGroup>
					<h3 v-text="$t('availabilityContact.subtitle')" />
					<SelectField
						v-model="form.accommodation"
						:options="properties"
						text-key="name"
						value-key="id"
						autocomplete
						:label="$t('availabilityContact.propertyName')"
						:errors="formErrors?.details?.accommodation" />
					<div class="formField textField" :class="{'hasValue': period, 'isError': formErrors?.period}">
						<div class="availabilityContact__FormDateField" @click="toggleDateModal">
							<label class="form__Label" v-text="$t('availabilityContact.chosenPeriod')" />
							<div class="textField__Wrapper">
								<p>{{period}}</p>
								<button v-if="period" type="button" @click="clearDate" aria-label="Clear"><Icon name="clear" /></button>
							</div>
						</div>
						<div v-if="formErrors?.period" class="form__Errors">
							<span class="form__Error">{{ formErrors?.period }}</span>
						</div>
					</div>

					<SelectField
						v-model="form.adults_count"
						:label="$t('availabilityContact.numberOfAdults')"
						:options="adultOptions"
						text-key="text"
						class="availabilityContact__FormGuestsDropdown">
					</SelectField>

					<SelectField
						v-model="children"
						:label="$t('availabilityContact.numberOfChildren')"
						:options="childrenOptions"
						text-key="text"
						class="availabilityContact__FormGuestsDropdown hasValue"
					>
					</SelectField>
					<div class="availabilityContact__FormChildrenAge" v-if="children >= 1">
						<SelectField
							v-for="(child, index) in children"
							:key="index"
							:label="$t('availabilityContact.childAge', index + 1)"
							v-model="form.children_age[index]"
							:options="childrenAge"
							text-key="value"
							value-key="value"
							:errors="formErrors?.children_age" />
					</div>
				</FieldGroup>

				<FieldGroup class="availabilityContact__FormContactSection">
					<h3 v-text="$t('availabilityContact.contactDetails')" />
					<TextField v-model="form.first_name" :label="$t('forms.firstName')" :errors="formErrors?.details?.first_name" />
					<TextField v-model="form.last_name" :label="$t('forms.lastName')" :errors="formErrors?.details?.last_name" />
					<TextField
						:ref="addField"
						v-model="form.email"
						:label="$t('forms.email')"
						type="email"
						:validate="{ type: ['required', 'email'], message: { required: $t('formErrors.required'), email: $t('formErrors.email') } }"
						:errors="formErrors?.details?.email" />
					<span>{{ $t('availabilityContact.confirmationEmail') }}</span>
					<TextField
						:ref="addField"
						v-model="confirm_email"
						:label="$t('forms.confirmEmail')"
						type="email"
						info="skuahdas"
						:validate="{
							type: ['required', 'email', 'sameAs'],
							compareValue: form.email,
							message: { required: $t('formErrors.required'), email: $t('formErrors.email'), sameAs: $t('formErrors.sameAsEmail') },
						}"
						:errors="formErrors?.details?.email" />
					<TextField v-model="form.address" :label="$t('forms.streetAddress')" :errors="formErrors?.details?.address" />
					<TextField v-model="form.city" :label="$t('forms.city')" :errors="formErrors?.details?.city" />
					<SelectField
						v-model="form.country"
						:options="countriesList"
						:label="$t('forms.country')"
						text-key="name"
						value-key="code"
						autocomplete
						:errors="formErrors?.details?.country" />
					<TextField v-model="form.zip" :label="$t('forms.postalCode')" :errors="formErrors?.details?.zip" />
					<PhoneNumberField v-model:numberValue="phoneNumber.phone_number" v-model:codeValue="phoneNumber.dial_code" :label="$t('forms.mobile')" />
					<TextareaField
						v-model="form.comment"
						:label="$t('availabilityContact.specialRequestLabel')"
						:placeholder="$t('availabilityContact.specialRequestPlaceholder')" />
				</FieldGroup>
				<FieldGroup>
					<CheckboxField
						:ref="addConditionalField"
						v-model="form.terms"
						:errors="formErrors?.terms">
						<i18n-t tag="p" scope="global" keypath="availabilityContact.checkbox">
							<template #linkConditions>
								<nuxt-link :to="link('GeneralConditions')" target="_blank">{{ $t('availabilityContact.linkConditions') }}</nuxt-link>
							</template>
							<template #linkPrivacy>
								<nuxt-link :to="link('PrivacyPolicy')" target="_blank">{{ $t('availabilityContact.linkPrivacy') }}</nuxt-link>
							</template>
						</i18n-t>
					</CheckboxField>
				</FieldGroup>
				<Button :disabled="isButtonDisabled" type="button" class="availabilityContact__FormSubmitButton" medium full-width>{{ $t('submit') }}</Button>
			</form>
		</div>

		<div class="availabilityContact__Success container" v-else>
			<Icon name="check-circle-fill" />
			<h2 v-text="$t('availabilityContact.successTitle')" />
			<p v-text="$t('availabilityContact.successLead')" />
			<NuxtLink :to="link('Home')" class="button--Primary">{{ $t('error.missingPageButton') }}</NuxtLink>
		</div>

		<Teleport to="body">
			<div class="availabilityContact__Datepicker" v-if="dateModal">
				<button class="availabilityContact__DatepickerClose" type="button" @click="toggleDateModal">
					Close
					<Icon name="close" />
				</button>
				<VueDatePicker
					class="reservationPanel__Calendar"
					v-model="datepickerDates"
					range
					:multi-calendars="{ count: 2 }"
					:clearable="false"
					:auto-apply="true"
					:enable-time-picker="false"
					month-name-format="long"
					:format="formatDatepickerDate"
					:hide-offset-dates="true"
					:min-date="new Date()"
					inline
					@update:model-value="updateDates">
					<template #arrow-left>
						<Icon name="arrow-left-circle" />
					</template>
					<template #input-icon>
						<img src="/resources/images/icons/calendar.svg" width="24" height="24" />
					</template>
					<template #day="{ day }">
						<div class="dp__cell__dayValue">
							{{ day }}
						</div>
					</template>
					<template #arrow-right>
						<Icon name="arrow-right-circle" />
					</template>
				</VueDatePicker>
			</div>
		</Teleport>
	</NuxtLayout>
</template>

<script setup>
	import VueDatePicker from '@vuepic/vue-datepicker';
	import { format, toZonedTime } from 'date-fns-tz';
	import countriesList from '~/data/countries';
	defineOptions({
    auth: 'aminess'
  });
	
	defineProps({
		page: {},
	});

	const { accommodationsData, link } = useSetup();
	const { t } = useI18n();
	const { user: userData } = useAuth('aminess');

	const formErrors = ref(null);
	const { customError, setCustomError } = useCustomFormErrors(formErrors.value);

	const dateModal = ref(false);
	const isMobile = ref(false);
	const isFormSuccess = ref(false);
	const field = ref([]);
	const conditionalField = ref([]);
	const period = ref(null);
	const datepickerDates = ref([]);
	const confirm_email = ref('');
	const isButtonDisabled = ref(true)

	const phoneNumber = ref({
		phone_number: '',
		dial_code: '+385'
	})

	const properties = computed(() => {
		return Object.values(accommodationsData.value).map((item) => ({
			name: item.name,
			id: item.id,
		}));
	});

	const adultOptions = computed(() => {
		let result = [];
		for (let i = 1; i <= 10; i++) result.push({ value: i, text: t('availabilityContact.guests', i) });
		return result;
	});

	const childrenOptions = computed(() => {
		let result = [];
		for (let i = 0; i <= 10; i++) result.push({ value: i, text: t('availabilityContact.guests', i) });
		return result;
	});

	const childrenAge = computed(() => {
		let result = [];
		for (let i = 1; i <= 16; i++) result.push({ value: i });
		return result;
	});

	const children = ref(childrenOptions.value[0].value);
	const form = ref({
		accommodation: '',
		adults_count: adultOptions.value[0].value,
		date_from: '',
		date_to: '',
		children_age: [],
		first_name: '',
		last_name: '',
		email: '',
		address: '',
		city: '',
		country: '',
		zip: '',
		comment: '',
		terms: false,
	});

	onMounted(() => {
		setTimeout(() => {
			if (userData.value) {
				const keysMapper = { 'first_name': 'first_name', 'last_name': 'last_name', 'email': 'email', 'address': 'address', 'city': 'city', 'country_code': 'country', 'postal_code': 'zip' };
				for (const item in keysMapper) {
					if (userData.value?.profile?.[item] && form.value.hasOwnProperty(keysMapper[item])) {
						form.value[keysMapper[item]] = userData.value.profile[item];
					}
				}
				
				for (const item of ['dial_code', 'phone_number']) {
					if (userData.value?.profile?.[item] && phoneNumber.value.hasOwnProperty(item)) {
						phoneNumber.value[item] = userData.value.profile[item];
					}
				}
			}
			handleResize();
		}, 1);
	});

	function addField(el) {
		if (el && !el?.added) {
			field.value.push(el);
			el.added = true;
		}
	}

	function addConditionalField(el) {
		if (el && !el?.added) {
			conditionalField.value.push(el);
			el.added = true;
		}
	}

	function clearDate() {
		period.value = null;
		datepickerDates.value = [];
		form.value.date_from = '';
		form.value.date_to = '';
	}

	async function submitEnquiry() {
		customError.value = null;
		let isValid = useFormIsValid(field.value);
		if (!isValid) {
			scrollToError();
			return;
		}
		
		isButtonDisabled.value = true;
		try {
			const response = await useClientFetch(`/availability-enquiry/accommodation/`, {
				method: 'POST',
				body: JSON.stringify(form.value),
			});
			isFormSuccess.value = true;
		} catch (error) {
			if (error.data) formErrors.value = error.data;
			if (formErrors.value?.details?.date_from && formErrors.value?.details?.date_to) {
				formErrors.value = { ...formErrors.value, period: t('formErrors.required') };
			}

			if(!form.value.terms) formErrors.value = {...formErrors.value, terms: t('formErrors.required')}
			setCustomError(formErrors.value);
			scrollToError();
		}
		isButtonDisabled.value = false;
	}

	function toggleDateModal() {
		dateModal.value = !dateModal.value;
		document.documentElement.classList[dateModal.value ? 'add' : 'remove']('isOverflow');
	}

	function formatDatepickerDate(date) {
		return `${formatDate(date[0])} - ${formatDate(date[1])}`;
	}

	function scrollToError() {
		setTimeout(() => {
			const elements = document.querySelectorAll('.formField.isError');
			if (elements.length) elements[0].scrollIntoView({ behavior: 'smooth' });
		}, 10);
	}

	function updateDates(dates) {
		period.value = formatDatepickerDate(dates);
		const jsDates = dates.map((date) => format(toZonedTime(new Date(date), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' }));
		form.value.date_from = jsDates[0];
		form.value.date_to = jsDates[1];

		toggleDateModal();
	}

	function handleResize() {
		isMobile.value = window.innerWidth <= 768;
	}

	watch(
		() => form.value.children,
		() => {
			if (form.value.children < form.value.children_age.length) form.value.children_age.pop();
			if (form.value.children === 0) form.value.children_age = [];
		}
	);

	watch(() => phoneNumber.value, (value) => {
    if (value.dial_code && value.phone_number) {
      form.value.phone = `${value.dial_code}${value.phone_number}`;
    } else {
      form.value.phone = '';
    }
  }, { deep: true, immediate: true });

	watch(() => form.value.terms, (value) => {
		isButtonDisabled.value = value ? false : true
  }, { deep: true, immediate: true });
</script>

<style lang="scss">
	@import url('@/assets/styles/pages/_availabilityContact.scss');
	@import url('@/assets/styles/components/_datePicker.scss');
</style>
